/*
 * Mapping Console — flat view of every indicator → mapping binding so
 * analysts can verify that an indicator actually has somewhere to land
 * (a publication target, a portal dataset, etc.). Mappings are
 * fetched from /api/mappings and joined to /api/indicators.
 *
 * Phase 6 will add inline preview + editor; this is the minimum
 * surface that makes a previously-hidden concern visible.
 */

import { useEffect, useMemo, useState } from "react";
import PageBreadcrumbs from "../layouts/PageBreadcrumbs.js";
import { api, ApiError } from "../lib/api.js";
import { SkeletonTable } from "../components/Skeleton.js";

type Mapping = Awaited<ReturnType<typeof api.listIndicatorMappings>>["mappings"][number];
type Indicator = Awaited<ReturnType<typeof api.listIndicators>>["indicators"][number];

export interface MappingConsoleProps {
  scope: "workspace" | "platform";
}

export default function MappingConsole({ scope }: MappingConsoleProps) {
  const [mappings, setMappings] = useState<Mapping[] | null>(null);
  const [indicators, setIndicators] = useState<Indicator[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    let cancelled = false;
    Promise.all([api.listIndicatorMappings(), api.listIndicators()])
      .then(([m, i]) => {
        if (cancelled) return;
        setMappings(m.mappings);
        setIndicators(i.indicators);
      })
      .catch((e) => { if (!cancelled) setError(e instanceof ApiError ? e.message : String(e)); });
    return () => { cancelled = true; };
  }, []);

  const indicatorById = useMemo(() => {
    const map = new Map<string, Indicator>();
    for (const i of indicators ?? []) map.set(i.id, i);
    return map;
  }, [indicators]);

  const rows = useMemo(() => {
    let r = mappings ?? [];
    if (search.trim()) {
      const q = search.toLowerCase();
      r = r.filter((m) => m.id.toLowerCase().includes(q) || m.indicatorId.toLowerCase().includes(q));
    }
    return r;
  }, [mappings, search]);

  const orphanIndicators = useMemo(() => {
    if (!indicators || !mappings) return [];
    const mapped = new Set(mappings.map((m) => m.indicatorId));
    return indicators.filter((i) => !mapped.has(i.id));
  }, [indicators, mappings]);

  return (
    <div className="page">
      <PageBreadcrumbs />
      <header className="page-head">
        <div>
          <h1>Mapping console</h1>
          <p className="muted">
            Indicator → publication mappings. Indicators without a mapping have no destination — they compute but don't ship anywhere.
          </p>
        </div>
      </header>

      {error && <div className="ts-alert ts-alert-error">{error}</div>}

      <section className="transfer-filters" aria-label="Mapping filters">
        <label className="transfer-filter transfer-filter-grow">
          Search
          <input type="search" placeholder="mapping id / indicator id…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </label>
      </section>

      {orphanIndicators.length > 0 && (
        <div className="ts-alert ts-alert-warning" role="status" style={{ marginBottom: 16 }}>
          <strong>{orphanIndicators.length}</strong> indicator(s) have no mapping and won't be published anywhere:
          <ul style={{ margin: "8px 0 0 20px", fontSize: 13 }}>
            {orphanIndicators.slice(0, 5).map((i) => (
              <li key={i.id} className="mono" style={{ fontSize: 12 }}>{i.name} <span className="muted">({i.id})</span></li>
            ))}
            {orphanIndicators.length > 5 && <li className="muted">…and {orphanIndicators.length - 5} more</li>}
          </ul>
        </div>
      )}

      {mappings === null && <SkeletonTable rows={5} columns={scope === "platform" ? 4 : 3} />}
      {mappings !== null && mappings.length === 0 && (
        <div className="empty-state-card">
          <h2 style={{ marginTop: 0 }}>No mappings configured</h2>
          <p>
            Mappings bind an indicator to a publication target (a DHIS2 dataset element, a portal dataset, or a webhook payload).
            Create a mapping in the embedded app's <strong>Mapping console</strong> to start publishing values.
          </p>
        </div>
      )}

      {rows.length > 0 && (
        <table className="transfer-table">
          <thead>
            <tr>
              <th>Mapping ID</th>
              <th>Indicator</th>
              {scope === "platform" && <th>Workspace</th>}
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((m) => {
              const ind = indicatorById.get(m.indicatorId);
              return (
                <tr key={m.id}>
                  <td className="mono" style={{ fontSize: 11 }}>{m.id}</td>
                  <td>
                    {ind ? (
                      <>
                        <div>{ind.name}</div>
                        <div className="mono" style={{ fontSize: 11, color: "var(--text-muted)" }}>{ind.id}</div>
                      </>
                    ) : (
                      <span className="pill pill-err">indicator missing</span>
                    )}
                  </td>
                  {scope === "platform" && <td className="mono" style={{ fontSize: 11 }}>{m.workspaceId ?? "—"}</td>}
                  <td>{ind ? <span className="pill pill-ok">bound</span> : <span className="pill pill-err">orphan</span>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
